import { ENV } from "../Utils/constants";

/**
 * Cliente API del panel admin transversal para LiveStreamGame (M13 F2.7).
 *
 * Endpoints bajo /api/v1/live-stream/admin/games protegidos por
 * requireAdmin en backend (role=admin).
 *
 * Token: usa el accessToken del admin logueado (Bearer Authorization).
 */
export class LiveGamesApi {
  baseApi = ENV.BASE_API;

  /** GET /live-stream/admin/games?includeDisabled=1 */
  async list(accessToken, includeDisabled = false) {
    const qs = includeDisabled ? "?includeDisabled=1" : "";
    const res = await fetch(`${this.baseApi}/live-stream/admin/games${qs}`, {
      headers: { Authorization: `Bearer ${accessToken}` },
    });
    const data = await res.json();
    if (!res.ok) throw data;
    return data.items || [];
  }

  /** POST /live-stream/admin/games */
  async create(accessToken, payload) {
    const res = await fetch(`${this.baseApi}/live-stream/admin/games`, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${accessToken}`,
      },
      body: JSON.stringify(payload),
    });
    const data = await res.json();
    if (!res.ok) throw data;
    return data.game;
  }

  /** PATCH /live-stream/admin/games/:id */
  async update(accessToken, id, patch) {
    const res = await fetch(`${this.baseApi}/live-stream/admin/games/${id}`, {
      method: "PATCH",
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${accessToken}`,
      },
      body: JSON.stringify(patch),
    });
    const data = await res.json();
    if (!res.ok) throw data;
    return data.game;
  }

  /** PATCH /live-stream/admin/games/:id/toggle */
  async toggle(accessToken, id) {
    const res = await fetch(
      `${this.baseApi}/live-stream/admin/games/${id}/toggle`,
      {
        method: "PATCH",
        headers: { Authorization: `Bearer ${accessToken}` },
      },
    );
    const data = await res.json();
    if (!res.ok) throw data;
    return data.game;
  }

  /** DELETE /live-stream/admin/games/:id?hard=1 */
  async remove(accessToken, id, hard = false) {
    const qs = hard ? "?hard=1" : "";
    const res = await fetch(
      `${this.baseApi}/live-stream/admin/games/${id}${qs}`,
      {
        method: "DELETE",
        headers: { Authorization: `Bearer ${accessToken}` },
      },
    );
    if (res.status === 204) return { ok: true, mode: hard ? "hard" : "soft" };
    const data = await res.json();
    if (!res.ok) throw data;
    return data;
  }

  /** GET /live-stream/admin/games/:id/session-count */
  async sessionCount(accessToken, id) {
    const res = await fetch(
      `${this.baseApi}/live-stream/admin/games/${id}/session-count`,
      {
        headers: { Authorization: `Bearer ${accessToken}` },
      },
    );
    const data = await res.json();
    if (!res.ok) throw data;
    return data.count;
  }

  /** POST /live-stream/admin/games/icon-upload-url */
  async signedIconUpload(accessToken, gameKey, contentType, ext) {
    const res = await fetch(
      `${this.baseApi}/live-stream/admin/games/icon-upload-url`,
      {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${accessToken}`,
        },
        body: JSON.stringify({ gameKey, contentType, ext }),
      },
    );
    const data = await res.json();
    if (!res.ok) throw data;
    return data; // { uploadUrl, publicUrl, objectName, expiresAt }
  }

  /** POST /live-stream/admin/games/:id/send-sdk */
  async sendSdk(accessToken, id, email, customMessage = "") {
    const res = await fetch(
      `${this.baseApi}/live-stream/admin/games/${id}/send-sdk`,
      {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${accessToken}`,
        },
        body: JSON.stringify({ email, customMessage }),
      },
    );
    const data = await res.json();
    if (!res.ok) throw data;
    return data; // { sent, to, gameKey, sentAt }
  }

  /**
   * Sube un File a GCS via PUT directo a la signed URL.
   * Devuelve la publicUrl que se debe guardar como iconUrl en el game.
   */
  async uploadIconFile(accessToken, gameKey, file) {
    const ext = (file.name.split(".").pop() || "png").toLowerCase();
    const contentType = file.type || "image/png";
    const signed = await this.signedIconUpload(
      accessToken,
      gameKey,
      contentType,
      ext,
    );
    const putRes = await fetch(signed.uploadUrl, {
      method: "PUT",
      headers: { "Content-Type": contentType },
      body: file,
    });
    if (!putRes.ok) {
      throw new Error(`GCS PUT fallo: HTTP ${putRes.status}`);
    }
    return signed.publicUrl;
  }
}
